import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getCustomers,
  getCustomer,
  createCustomer as createCustomerApi,
  updateCustomer as updateCustomerApi,
  getCustomerOrders,
  getCustomerUsers,
  archiveCompanyUser,
  unarchiveCompanyUser,
  createAddress,
  updateAddress,
  deleteAddress,
  setDefaultAddress,
  createNote,
  updateNote,
  deleteNote,
} from '../services/customers';
import { logError } from '../utils/errorHandler';

/**
 * Custom hook for managing customer (company) state in the employee portal.
 *
 * @param {Object} options
 * @param {boolean} options.autoFetch - Fetch companies on mount (default: true)
 * @returns {Object} State and action functions
 */
const useCustomers = ({ autoFetch = true } = {}) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(autoFetch);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ count: 0, page: 1 });
  const hasLoadedRef = useRef(false);

  /**
   * Wraps a mutating async operation with isSaving + error state management.
   * Eliminates the repetitive try/catch/finally pattern across all mutations.
   */
  const withSave = useCallback(async (fn) => {
    setIsSaving(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  /**
   * Fetch paginated customer list
   */
  const fetchCustomers = useCallback(async (params = {}) => {
    try {
      setError(null);
      if (hasLoadedRef.current) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      const data = await getCustomers(params);
      setCustomers(data.results || []);
      setPagination({
        count: data.count || 0,
        page: params.page || 1,
      });
    } catch (err) {
      logError('Fetch customers', err);
      setError(err);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  /**
   * Fetch a single customer (company) by ID, including addresses + notes.
   * Uses its own loading state rather than the shared list `loading` flag
   * to avoid interfering with the customer list UI.
   */
  const fetchCustomer = useCallback(async (id) => {
    try {
      setError(null);
      const data = await getCustomer(id);
      return data;
    } catch (err) {
      logError('Fetch customer', err);
      setError(err);
      throw err;
    }
  }, []);

  /**
   * Fetch orders for a company
   */
  const fetchOrders = useCallback(async (id) => {
    try {
      setError(null);
      const data = await getCustomerOrders(id);
      return data;
    } catch (err) {
      logError('Fetch customer orders', err);
      setError(err);
      throw err;
    }
  }, []);

  /**
   * Fetch users/contacts for a company. Pass { includeArchived: true } to
   * also fetch archived members (used by the "show archived" toggle).
   */
  const fetchUsers = useCallback(async (id, options = {}) => {
    try {
      setError(null);
      const data = await getCustomerUsers(id, options);
      return data;
    } catch (err) {
      logError('Fetch customer users', err);
      setError(err);
      throw err;
    }
  }, []);

  const archiveUser = useCallback(
    (profileId, reason) => withSave(() => archiveCompanyUser(profileId, reason)),
    [withSave],
  );

  const unarchiveUser = useCallback(
    (profileId) => withSave(() => unarchiveCompanyUser(profileId)),
    [withSave],
  );

  // ── Address mutations ──────────────────────────────────────────────────────

  const addAddress = useCallback(
    (addressData) => withSave(() => createAddress(addressData)),
    [withSave],
  );

  const editAddress = useCallback(
    (addressId, addressData) =>
      withSave(() => updateAddress(addressId, addressData)),
    [withSave],
  );

  const removeAddress = useCallback(
    (addressId) => withSave(() => deleteAddress(addressId)),
    [withSave],
  );

  const makeDefaultAddress = useCallback(
    (addressId) => withSave(() => setDefaultAddress(addressId)),
    [withSave],
  );

  // ── Customer mutations ───────────────────────────────────────────────────────

  const createCustomer = useCallback(
    (data) => withSave(() => createCustomerApi(data)),
    [withSave],
  );

  const updateCustomer = useCallback(
    (companyId, data) =>
      withSave(() => updateCustomerApi(companyId, data)),
    [withSave],
  );

  // ── Note mutations ─────────────────────────────────────────────────────────

  const addNote = useCallback(
    (companyId, body) =>
      withSave(() => createNote({ company: companyId, body })),
    [withSave],
  );

  const editNote = useCallback(
    (noteId, body) => withSave(() => updateNote(noteId, body)),
    [withSave],
  );

  const removeNote = useCallback(
    (noteId) => withSave(() => deleteNote(noteId)),
    [withSave],
  );

  const clearError = useCallback(() => setError(null), []);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchCustomers();
    }
  }, [autoFetch, fetchCustomers]);

  return {
    customers,
    loading,
    isRefreshing,
    isSaving,
    error,
    pagination,
    fetchCustomers,
    fetchCustomer,
    fetchOrders,
    fetchUsers,
    archiveUser,
    unarchiveUser,
    createCustomer,
    updateCustomer,
    addAddress,
    editAddress,
    removeAddress,
    makeDefaultAddress,
    addNote,
    editNote,
    removeNote,
    clearError,
  };
};

export default useCustomers;
